<template>
  <div class="pageRoot linkListPage">
    <div ref="pageCard" class="pageShell">
      <div ref="pageCardMain" class="pageShell__main">
        <div
          ref="scrollArea"
          class="pageShell__scroll"
          :class="{ 'pageShell__scroll--drawerOpen': filterDrawerOpen }"
        >
          <div
            class="listWorkspace"
            :class="{ 'listWorkspace--scrolled': controlBarScrolled }"
          >
            <section
              ref="filterAnchor"
              class="filterCard filterAnchor"
              :class="{ 'filterAnchor--drawerOpen': filterDrawerOpen }"
            >
              <ai-style-list-filter-panel
                v-bind="filterPanelProps"
                v-on="filterPanelListeners"
              />
            </section>

            <section
              ref="tableZone"
              class="tableCard tableZone"
              :class="{ 'tableCard--scrolled': controlBarScrolled }"
            >
              <div
                class="scrollControlBar"
                :class="{ 'scrollControlBar--scrolled': controlBarScrolled }"
              >
                <div class="scrollControlBar__lead">
                  <span class="scrollControlBar__label">共</span>
                  <strong class="scrollControlBar__count">{{ listTotalCount }}</strong>
                  <span class="scrollControlBar__label">条</span>
                </div>

                <div
                  v-if="!controlBarScrolled"
                  class="scrollControlBar__stage scrollControlBar__stage--full"
                >
                  <control-bar-display
                    layout="info"
                    :scope-filters="activeScopeFilters"
                    :has-active-scope-filters="hasActiveScopeFilters"
                    :scope-filter-tooltip="scopeFilterTooltip"
                    :selection-count="0"
                    @open-filter="openFilterDrawer"
                  />
                </div>

                <template v-else>
                  <div class="scrollControlBar__stage scrollControlBar__stage--left">
                    <control-bar-display
                      part="info"
                      layout="info"
                      :scope-filters="activeScopeFilters"
                      :has-active-scope-filters="hasActiveScopeFilters"
                      :scope-filter-tooltip="scopeFilterTooltip"
                      :selection-count="0"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__navSlot">
                    <control-bar-nav
                      @scroll-top="scrollToTableTop"
                      @open-filter="openFilterDrawer"
                    />
                  </div>
                  <div class="scrollControlBar__stage scrollControlBar__stage--right" />
                </template>

                <div class="scrollControlBar__page">
                  <template v-if="listTotalCount > 0">
                    <span>第 {{ listRangeStart }}–{{ listRangeEnd }} 条</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                  <template v-else>
                    <span class="scrollControlBar__emptyHint">暂无数据</span>
                    <span class="scrollControlBar__metaSep" aria-hidden="true">·</span>
                    <span>第 {{ currentPage }} / {{ totalPages }} 页</span>
                  </template>
                </div>
              </div>

              <div v-if="!loading && tableData.length" class="tableSection">
                <el-table
                  ref="multipleTable"
                  class="linkTable linkTable--hybrid"
                  :data="tableData"
                  tooltip-effect="dark"
                  style="width: 100%"
                  row-key="id"
                >
                  <el-table-column
                    label="AppKey"
                    prop="appKey"
                    align="center"
                    header-align="center"
                    min-width="120"
                    show-overflow-tooltip
                  />
                  <el-table-column
                    label="分类"
                    prop="type"
                    align="center"
                    header-align="center"
                    width="120"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span
                        class="typeTag"
                        :class="'typeTag--' + getTypeVariant(scope.row.type)"
                      >
                        {{ getTypeLabel(scope.row.type) }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="排序"
                    prop="sort"
                    align="center"
                    header-align="center"
                    width="72"
                    show-overflow-tooltip
                  />
                  <el-table-column
                    label="风格"
                    prop="style"
                    align="center"
                    header-align="center"
                    min-width="120"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="nameCell">{{ scope.row.style || '—' }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="风格描述"
                    prop="styleDescription"
                    align="center"
                    header-align="center"
                    min-width="160"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <span class="cellMuted">{{ scope.row.styleDescription || '—' }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="风格预览图"
                    prop="stylePreview"
                    align="center"
                    header-align="center"
                    width="120"
                  >
                    <template slot-scope="scope">
                      <img
                        v-if="scope.row.stylePreview"
                        :src="scope.row.stylePreview"
                        alt="stylePreview"
                        class="stylePreviewImg"
                      />
                      <span v-else class="cellMuted">—</span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="状态"
                    prop="status"
                    align="center"
                    header-align="center"
                    width="100"
                  >
                    <template slot-scope="scope">
                      <span
                        class="statusPill"
                        :class="
                          scope.row.status === 1
                            ? 'statusPill--online'
                            : 'statusPill--offline'
                        "
                      >
                        <span class="statusPill__dot" aria-hidden="true" />
                        {{ getStatusLabel(scope.row.status) }}
                      </span>
                    </template>
                  </el-table-column>
                  <el-table-column
                    label="更新时间"
                    prop="updateTime"
                    align="center"
                    header-align="center"
                    min-width="160"
                    show-overflow-tooltip
                  />
                  <el-table-column
                    label="操作"
                    label-class-name="linkListActionColumnHead"
                    align="center"
                    header-align="center"
                    width="180"
                    class-name="actionColumn"
                  >
                    <template slot="header">
                      <span class="linkListActionColumnHead__label">操作</span>
                    </template>
                    <template slot-scope="scope">
                      <div class="linkTableActionCell">
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost"
                          icon="el-icon-edit"
                          @click.stop="goEdit(scope.row.id)"
                        >
                          编辑
                        </el-button>
                        <el-button
                          size="small"
                          class="rowActionBtn rowActionBtn--ghost rowActionBtn--danger"
                          icon="el-icon-delete"
                          @click.stop="deleteItem(scope.row.id)"
                        >
                          删除
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
              </div>

              <div v-else-if="!loading && !tableData.length" class="tableEmpty">
                <div class="tableEmpty__icon"><i class="el-icon-brush" /></div>
                <p class="tableEmpty__title">暂无 AI 风格</p>
                <p class="tableEmpty__desc">调整筛选条件或新增 AI 风格。</p>
                <el-button
                  type="primary"
                  size="small"
                  v-check-role
                  @click="$router.push('/config/aiStyle/create')"
                >
                  新增 AI 风格
                </el-button>
              </div>

              <div v-else class="tableSkeletonWrap">
                <el-skeleton :rows="8" animated />
              </div>
            </section>
          </div>
        </div>

        <div
          v-if="filterDrawerOpen"
          class="filterDrawerLayer"
          role="dialog"
          aria-modal="true"
          aria-label="筛选与操作"
        >
          <div
            class="filterDrawerPanel"
            :class="{ 'filterDrawerPanel--scrollable': drawerPanelScrollable }"
            :style="drawerPanelStyle"
          >
            <div class="filterDrawerPanel__header">
              <span class="filterDrawerPanel__title">筛选与操作</span>
              <div class="filterDrawerPanel__headerActions">
                <span v-if="activeFilterCount" class="filterDrawerPanel__chip">
                  已筛选 {{ activeFilterCount }} 项
                </span>
                <button
                  type="button"
                  class="filterDrawerPanel__closeBtn"
                  @click="closeFilterDrawer"
                >
                  收起
                </button>
              </div>
            </div>
            <div ref="drawerScroll" class="filterDrawerPanel__scroll">
              <ai-style-list-filter-panel
                v-bind="{ ...filterPanelProps, showPanelSummary: true }"
                v-on="filterPanelListeners"
              />
            </div>
          </div>
          <div
            class="filterDrawerBackdrop"
            aria-hidden="true"
            @click="closeFilterDrawer"
            @wheel.prevent
          />
        </div>
      </div>

      <div class="pageShell__footer tableCard__footer footerBar">
        <div class="footerSummary">
          共 <strong class="footerSummary__count">{{ listTotalCount }}</strong> 条 · 第
          {{ currentPage }} / {{ totalPages }} 页
        </div>
        <el-pagination
          class="linkPagination"
          layout="prev, pager, next, sizes, jumper"
          :total="listTotalCount"
          :page-sizes="[25, 50, 100]"
          :page-size="currentPageSize"
          :current-page.sync="currentPage"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getAiStyleList, deleteAiStyle } from '../../../api/aiStyle'
import AiStyleListFilterPanel from './AiStyleListFilterPanel.vue'
import ControlBarDisplay from '../../resourceManage/genericResourceManage/ControlBarDisplay.vue'
import ControlBarNav from '../../resourceManage/genericResourceManage/ControlBarNav.vue'
import linkListScrollControlMixin from '../../resourceManage/genericResourceManage/linkListScrollControlMixin'

const TYPE_LABELS = {
  1: '文生图',
  2: '图生图',
  3: '角色文生图',
  4: '角色图生图',
}

const TYPE_VARIANTS = ['violet', 'teal', 'amber', 'rose']

export default {
  name: 'AiStyleList',
  components: {
    AiStyleListFilterPanel,
    ControlBarDisplay,
    ControlBarNav,
  },
  mixins: [linkListScrollControlMixin],
  data() {
    return {
      appKey: '',
      version: '',
      type: '',
      style: '',
      status: '',
      tableData: [],
      currentPage: 1,
      currentPageSize: 25,
      total: 0,
      loading: false,
    }
  },
  computed: {
    listTotalCount() {
      const parsed = Number(this.total)
      return Number.isFinite(parsed) ? parsed : 0
    },
    listRangeStart() {
      if (this.listTotalCount === 0) return 0
      return (this.currentPage - 1) * this.currentPageSize + 1
    },
    listRangeEnd() {
      if (this.listTotalCount === 0) return 0
      return Math.min(this.currentPage * this.currentPageSize, this.listTotalCount)
    },
    totalPages() {
      if (this.listTotalCount === 0) return 1
      return Math.max(1, Math.ceil(this.listTotalCount / this.currentPageSize))
    },
    activeFilterCount() {
      return [this.appKey, this.type, this.version, this.style, this.status].filter(
        (value) => value !== '' && value !== null && value !== undefined
      ).length
    },
    activeScopeFilters() {
      const chips = []
      if (String(this.appKey || '').trim()) {
        chips.push({ key: 'appKey', label: 'AppKey', value: this.appKey.trim() })
      }
      if (String(this.type || '').trim()) {
        chips.push({
          key: 'type',
          label: '分类',
          value: this.getTypeLabel(this.type),
        })
      }
      if (String(this.version || '').trim()) {
        chips.push({ key: 'version', label: '版本', value: this.version.trim() })
      }
      if (String(this.style || '').trim()) {
        chips.push({ key: 'style', label: '风格', value: this.style.trim() })
      }
      if (this.status !== '' && this.status != null) {
        chips.push({
          key: 'status',
          label: '状态',
          value: this.getStatusLabel(this.status),
        })
      }
      return chips
    },
    hasActiveScopeFilters() {
      return this.activeScopeFilters.length > 0
    },
    scopeFilterTooltip() {
      return this.activeScopeFilters
        .map((item) => `${item.label}: ${item.value}`)
        .join(' · ')
    },
    filterPanelProps() {
      return {
        appKey: this.appKey,
        type: this.type,
        version: this.version,
        style: this.style,
        status: this.status,
        activeFilterCount: this.activeFilterCount,
        panelTotalCount: this.listTotalCount,
      }
    },
    filterPanelListeners() {
      return {
        search: this.search,
        'go-create': () => this.$router.push('/config/aiStyle/create'),
        'reset-filters': this.resetFilters,
        'reset-and-search': this.resetAndSearch,
        'update:appKey': (value) => {
          this.appKey = value
        },
        'update:type': (value) => {
          this.type = value
        },
        'update:version': (value) => {
          this.version = value
        },
        'update:style': (value) => {
          this.style = value
        },
        'update:status': (value) => {
          this.status = value
        },
      }
    },
  },
  watch: {
    loading(value) {
      if (!value) {
        this.onLinkListDataRendered()
      } else {
        this.$nextTick(() => {
          this.measureTableZoneWidth()
        })
      }
    },
    tableData() {
      this.$nextTick(() => {
        this.remeasureFilterBlockHeight()
        this.updateScrollControls()
        this.scheduleTableLayout()
      })
    },
    filterDrawerOpen(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.syncDrawerLayout()
        })
      }
    },
  },
  created() {
    this.bindLinkListScrollControl()
    this.getList()
  },
  methods: {
    getTypeLabel(type) {
      return TYPE_LABELS[type] || TYPE_LABELS[String(type)] || '—'
    },
    getTypeVariant(type) {
      const index = Number(type) - 1
      if (index >= 0 && index < TYPE_VARIANTS.length) {
        return TYPE_VARIANTS[index]
      }
      return 'neutral'
    },
    getStatusLabel(status) {
      if (status === 1) return '上线'
      if (status === 0) return '下线'
      return '—'
    },
    getList() {
      this.loading = true
      const params = {
        appKey: this.appKey,
        version: this.version,
        type: this.type,
        pageNum: this.currentPage,
        pageSize: this.currentPageSize,
        status: this.status,
        style: this.style,
      }
      getAiStyleList(params)
        .then((res) => {
          if (res.success) {
            this.total = res.total
            this.tableData = res.data || []
          }
        })
        .catch((err) => {
          console.log(err)
        })
        .finally(() => {
          this.loading = false
          this.$nextTick(() => {
            this.measureTableZoneWidth()
            this.scheduleTableLayout()
          })
        })
    },
    search() {
      this.currentPage = 1
      this.getList()
    },
    resetFilters() {
      this.appKey = ''
      this.version = ''
      this.type = ''
      this.style = ''
    },
    resetAndSearch() {
      this.resetFilters()
      this.search()
    },
    handleSizeChange(val) {
      this.currentPageSize = val
      this.$nextTick(() => {
        this.getList()
      })
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.getList()
    },
    goEdit(id) {
      window.open(`/#/config/aiStyle/edit/${id}`, '_blank')
    },
    deleteItem(id) {
      this.$confirm('是否要对该元素进行删除操作?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      })
        .then(() => {
          deleteAiStyle({ id }).then((res) => {
            if (res.success) {
              this.$message.success('删除成功!')
              this.getList()
            }
          })
        })
        .catch(() => {
          this.$message({
            type: 'info',
            message: '已取消删除',
          })
        })
    },
  },
}
</script>

<style scoped>
.cellMuted {
  color: var(--link-ink-secondary);
}

.stylePreviewImg {
  width: 72px;
  max-width: 100%;
  border-radius: 8px;
  object-fit: cover;
}
</style>
